import { ReactComponent as UpwardIcon } from "../assets/icons/upward.svg";

export type TrendInfo = {
  trend?: string;
  text?: string;
};

const DEFAULT_POSITIVE_TRENDS = ["up", "equal", ""];
const GREEN = "text-[#14AE5C]";
const GREEN_STROKE = "[&_path]:stroke-[#14AE5C]";
const RED = "text-[#FF4747]";
const RED_STROKE = "[&_path]:stroke-[#FF4747]";

export type TrendIndicatorProps = {
  /** Trend data: { trend: 'up' | 'down' | 'equal' | '', text: e.g. '5% from last week' or '2 places from last week' } */
  trendInfo: TrendInfo | null | undefined;
  /**
   * How to split and format the text. "percent" splits by "% " and appends "%"; "places" splits by "places " and appends " places".
   * @default "percent"
   */
  denominator?: "percent" | "places";
  /** Fallback for the second part of the text (e.g. " from last week"). */
  suffixFallback?: string;
  /** Icon size in px. */
  iconSize?: 14 | 15;
  /** Whether to rotate the arrow 90° when trend is down. */
  rotateIconWhenDown?: boolean;
  /** Trend values treated as positive (green). Default includes "" so unknown is green. */
  positiveTrends?: string[];
  /** Optional className for the wrapper div. */
  className?: string;
  /** Optional className for the text container (e.g. text-[13px] or text-xs). */
  textClassName?: string;
};

function parseTrendText(
  text: string | undefined,
  denominator: "percent" | "places"
): { valuePart: string; suffixPart: string } {
  const sep = denominator === "places" ? "places " : "% ";
  const parts = text?.split(sep) ?? [];
  const valuePart = parts[0]?.trim() ? `${parts[0]}${denominator === "places" ? " places" : "%"}` : denominator === "places" ? "0 places" : "0%";
  const suffixPart = parts[1]?.trim() ?? "";
  return { valuePart, suffixPart };
}

export function TrendIndicator({
  trendInfo,
  denominator = "percent",
  suffixFallback = " from last week",
  iconSize = 15,
  rotateIconWhenDown = true,
  positiveTrends = DEFAULT_POSITIVE_TRENDS,
  className = "",
  textClassName = "text-[#969696] text-[13px]",
}: TrendIndicatorProps) {
  if (trendInfo == null) return null;

  const trend = trendInfo.trend ?? "";
  const isPositive = positiveTrends.includes(trend);
  const { valuePart, suffixPart } = parseTrendText(trendInfo.text, denominator);
  const displayValue = valuePart || (denominator === "places" ? "0 places" : "0%");
  const displaySuffix = suffixPart || suffixFallback;

  const iconSizeClass = iconSize === 14 ? "w-[14px] h-[14px]" : "w-[15px] h-[15px]";
  const colorClass = isPositive ? GREEN : RED;
  const strokeClass = isPositive ? GREEN_STROKE : RED_STROKE;
  const rotateClass = !isPositive && rotateIconWhenDown ? "rotate-90" : "";

  return (
    <div className={`flex gap-2 items-center ${className}`}>
      <UpwardIcon className={`${iconSizeClass} ${strokeClass} ${rotateClass}`} />
      <div className={textClassName}>
        <span className={`font-medium ${colorClass}`}>{displayValue}</span>{" "}
        <span>{displaySuffix}</span>
      </div>
    </div>
  );
}
